import * as api from '@gitbook/api-client';
import { Icon } from '@gitbook/spine-icons';
import {
    Badge,
    type BadgeKind,
    type BadgeSize,
    CircularProgress,
    type IconDefinition,
    Tooltip,
} from '@gitbook/spine-react';

import {
    getTranslationEstimatedTimeRemaining,
    useTranslationProgress,
} from './useTranslationProgress';

type TranslationStatusBadgeFn<T> = (input: {
    translation: api.Translation;
    progress: api.TranslationRunChannelData['data'] | null;
}) => T;

const statusBadgeColors: Record<api.TranslationStatus, BadgeKind> = {
    [api.TranslationStatus.Pending]: 'warning',
    [api.TranslationStatus.Running]: 'info',
    [api.TranslationStatus.Completed]: 'done',
};

const statusBadgeLabels: Record<api.TranslationStatus, TranslationStatusBadgeFn<string>> = {
    [api.TranslationStatus.Pending]: () => 'Pending',
    [api.TranslationStatus.Running]: ({ progress }) =>
        `Running (${(progress?.progress ?? 0).toFixed(0)}%)`,
    [api.TranslationStatus.Completed]: () => 'Up-to-date',
};

const statusBadgeIcons: Record<api.TranslationStatus, TranslationStatusBadgeFn<IconDefinition>> = {
    [api.TranslationStatus.Pending]: () => Icon.Clock,
    [api.TranslationStatus.Running]: ({ progress }) => (
        <CircularProgress
            segments={[{ value: progress?.progress ?? 0, kind: 'done' }]}
            className="size-4"
        />
    ),
    [api.TranslationStatus.Completed]: () => Icon.Check,
};

const statusBadgeTooltip: Record<api.TranslationStatus, TranslationStatusBadgeFn<string>> = {
    [api.TranslationStatus.Pending]: () => 'The translation has not started yet.',
    [api.TranslationStatus.Running]: ({ progress }) =>
        `The latest version of the content is being translated. It might take a few minutes. Estimated time remaining: ${progress ? getTranslationEstimatedTimeRemaining(progress) : 'Unknown'}`,
    [api.TranslationStatus.Completed]: () =>
        'The latest version of the content has been translated.',
};

/**
 * A badge that displays a translation status.
 */
export function TranslationStatusBadge(props: { translation: api.Translation; size?: BadgeSize }) {
    const { translation, size } = props;

    const progress = useTranslationProgress(translation.id, {
        skip: translation.status !== api.TranslationStatus.Running,
    });

    return (
        <Tooltip label={statusBadgeTooltip[translation.status]({ translation, progress })}>
            <Badge
                icon={statusBadgeIcons[translation.status]({ translation, progress })}
                kind={statusBadgeColors[translation.status]}
                label={statusBadgeLabels[translation.status]({ translation, progress })}
                size={size}
            />
        </Tooltip>
    );
}
